<script setup lang="ts">
// 3D coverflow selector for the KDM matrix view, powered by Swiper.
// Replaces the hand-rolled CSS carousel: reliable click-to-select, prev/next
// navigation, keyboard and touch — same props/emit contract as before.
import {ref, watch, nextTick} from 'vue';
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Navigation, Keyboard, A11y} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const props = defineProps<{
  items: Array<{ id: string; title: string; isAssigned?: boolean }>;
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const modules = [Navigation, Keyboard, A11y];
// Swiper instance (typed loosely: vue-tsc is not part of the build pipeline).
const swiper = ref<any>(null);

const indexOfModel = (): number => {
  const i = props.items.findIndex((it) => it.id === props.modelValue);
  return i >= 0 ? i : 0;
};

function onSwiper(sw: any) {
  swiper.value = sw;
}

// Selection is driven by swipe / nav arrows / keyboard / CLICK: whichever slide
// ends up centred becomes the active item.
function onSlideChange(sw: any) {
  const item = props.items[sw.activeIndex];
  if (item && item.id !== props.modelValue) emit('update:modelValue', item.id);
}

// Click a card to select it (centre it via slideTo, which also fires
// onSlideChange). Far more intuitive than swipe-only on desktop.
function selectSlide(index: number): void {
  if (swiper.value) {
    swiper.value.slideTo(index, 250);
  }
  const item = props.items[index];
  if (item && item.id !== props.modelValue) {
    emit('update:modelValue', item.id);
  }
}

// Keep the carousel centered when the selection changes from outside.
watch(() => props.modelValue, () => {
  if (swiper.value && swiper.value.activeIndex !== indexOfModel()) {
    swiper.value.slideTo(indexOfModel(), 200);
  }
});

// Re-sync after items are added/removed.
watch(() => props.items.length, async () => {
  await nextTick();
  if (swiper.value) {
    swiper.value.update();
    swiper.value.slideTo(indexOfModel(), 0);
  }
});
</script>

<template>
  <Swiper
    class="matrix-axis-carousel"
    :modules="modules"
    :grab-cursor="true"
    :centered-slides="true"
    slides-per-view="auto"
    :space-between="14"
    :initial-slide="indexOfModel()"
    :slide-to-clicked-slide="true"
    :navigation="true"
    :keyboard="{ enabled: true }"
    @swiper="onSwiper"
    @slide-change="onSlideChange"
  >
    <SwiperSlide v-for="(item, index) in items" :key="item.id" class="matrix-axis-carousel__slide">
      <div
        class="matrix-axis-carousel__card"
        :class="{ 'is-active': item.id === modelValue }"
        @click="selectSlide(index)"
      >
        <i v-if="item.isAssigned" class="bi bi-person-check-fill" aria-hidden="true"></i>
        <span class="matrix-axis-carousel__title">{{ item.title }}</span>
      </div>
    </SwiperSlide>
  </Swiper>
</template>

<style scoped>
.matrix-axis-carousel {
  margin: 1rem 0;
  padding: 0 2.5rem; /* leave room for the nav arrows */
  --swiper-navigation-size: 24px;
  --swiper-navigation-color: #6c757d;
}

.matrix-axis-carousel__slide {
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.matrix-axis-carousel__card {
  width: 200px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.4rem;
  background: #f8f9fa;
  color: #495057;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border: 2px solid #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

.matrix-axis-carousel__card.is-active {
  background: #ffffff;
  border-color: #6c757d;
  color: #212529;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.matrix-axis-carousel__title {
  font-size: 0.9rem;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
